/**
 * The shared face routine. Mr. Clippy's eyes (white iris with a 1px ink outline, ink pupil, a blink
 * that closes to a line, pupils that track a target with a 60ms lag between the eyes, the wide /
 * squint / sparkle / double-blink / half-lid / glance / exit / wink states) live here so any
 * character draws the same eyes: Mr. Clippy and Foxy share this one code path.
 * Geometry is in the caller's current context units; `px` is one CSS pixel in them.
 */
export type EyeMode = 'neutral' | 'look' | 'wide' | 'squint' | 'sparkle' | 'double-blink' | 'half-lid' | 'glance' | 'exit' | 'wink' | 'slow-blink';
export interface Eyes {
  mode: EyeMode;
  /** For look / glance: where to look, as a unit vector (−1..1 each way, +y down, screen space). */
  target?: { x: number; y: number };
  /** Change this to retrigger a one-shot (wide, sparkle, double-blink, glance, wink). */
  key?: string | number;
}

/** A tween that starts from wherever the previous one is: the vocabulary's --ease-out, in JS. */
export class Tween {
  private from: number;
  private to: number;
  private t0 = 0;
  private dur = 0;
  constructor(v: number) {
    this.from = v;
    this.to = v;
  }
  set(to: number, now: number, dur: number) {
    if (to === this.to) return;
    this.from = this.at(now);
    this.to = to;
    this.t0 = now;
    this.dur = dur;
  }
  at(now: number) {
    if (this.dur <= 0) return this.to;
    const x = Math.min(1, Math.max(0, (now - this.t0) / this.dur));
    const e = 1 - Math.pow(1 - x, 3);
    return this.from + (this.to - this.from) * e;
  }
}

export interface EyeFrame {
  lid: number; // 0 open .. 1 closed (blink included)
  wink: number; // 0..1 second eye closed
  px: number; // pupil offset, fraction of r (first eye)
  py: number;
  pxR: number; // second eye, 60ms behind
  pyR: number;
  scale: number; // iris scale (wide 1.3)
  pupil: number; // pupil scale (wide .8)
  brow: number; // 0..1 squint brows
  spark: number; // 0..1 sparkle
  blinking: boolean;
  look: { x: number; y: number }; // the resolved look vector this frame
}

/** Persistent per-character eye state: tweens, the blink schedule and one-shot timing. */
export class EyeRig {
  private lid = new Tween(0);
  private px = new Tween(0);
  private py = new Tween(0);
  private pxR = new Tween(0);
  private pyR = new Tween(0);
  private scale = new Tween(1);
  private pupil = new Tween(1);
  private brow = new Tween(0);
  private spark = new Tween(0);
  private wink = new Tween(0);
  private nextBlink = 2.5;
  private blinkAt = -1;
  private blinks = 0;
  private shotKey: string | number | undefined = undefined;
  private shotAt = -1;

  /** Resolve the vocabulary for this frame. `t` is the loop's seconds, `now` performance.now(). */
  update(eyes: Eyes | undefined, skeptic: number, t: number, now: number, reduced: boolean): EyeFrame {
    const mode = eyes?.mode ?? 'neutral';
    if (eyes?.key !== this.shotKey) {
      this.shotKey = eyes?.key;
      this.shotAt = now;
      this.blinks = 0;
    }
    const since = now - this.shotAt;
    let lid = skeptic * 0.35;
    let look = { x: 0, y: 0 };
    let scale = 1;
    let pupil = 1;
    let brow = 0;
    let spark = 0;
    let wink = 0;
    const tgt = eyes?.target ?? { x: 0, y: 0 };
    // EVERY mode keeps the caller's target as its pupil direction. A mode says what the face is
    // DOING; the target says where it is LOOKING; the two compose. `exit` is the one exception —
    // it looks away by definition. Before 2026-11-02 only `look` and `glance` read the target, so
    // a sentence cue arriving while the character was looking at something silently re-centred the
    // pupils: Foxy's eyes left the thing she was pointing at the moment she said anything about it
    // (lane 9 review F1). Measured on the real canvas, pupil centroid vs eye centre, CSS px:
    // `look` -2.25/+0.69 but `sparkle` with the SAME target 0.00/-0.02 — dead centre. After this
    // change `sparkle` with that target reads -2.18/+0.68, i.e. the look survives the cue, and a
    // cue with NO target is still exactly centred. Callers have always passed a target on every mode — guide/stage.tsx
    // builds `toward` once and attaches it to whatever mode the beat asked for — so this is the
    // rig catching up with its callers, not a new capability.
    look = tgt;
    // `glance` is the one mode that still releases the target: a flick toward something and back,
    // 450ms. Kept deliberately (owner call, 2026-11-02) — it is the only one whose whole meaning
    // is the RETURN. Consequence in the meeting room, stated rather than discovered: a `glance`
    // cue arriving while lane 9 holds a stage look ends that look 450ms in. Acceptable, because a
    // glance IS a brief look toward the stage.
    if (mode === 'glance') look = since < 450 ? tgt : { x: 0, y: 0 };
    else if (mode === 'wide') { scale = since < 280 ? 1.3 : 1; pupil = since < 280 ? 0.8 : 1; lid = 0; }
    else if (mode === 'squint') { lid = 0.45; brow = 1; }
    else if (mode === 'half-lid') lid = Math.max(lid, 0.2);
    else if (mode === 'sparkle') { spark = since < 120 ? 1 : since < 300 ? 1 - (since - 120) / 180 : 0; lid = 0; }
    else if (mode === 'exit') look = { x: 0.8, y: -0.8 };
    else if (mode === 'wink') wink = since < 180 ? 1 : 0;
    else if (mode === 'slow-blink') lid = since > 60 && since < 360 ? 1 : 0; // one slow blink: the lids ease shut and open again over ~600ms
    const blinkEvery = mode === 'half-lid' ? 7 : 4 + ((Math.sin(this.nextBlink * 12.9898) + 1) / 2) * 2; // 4–6s
    if (!reduced && t > this.nextBlink && this.blinkAt < 0) {
      this.blinkAt = t;
      this.nextBlink = t + blinkEvery;
    }
    if (mode === 'double-blink' && !reduced && this.blinks < 2 && this.blinkAt < 0 && since >= this.blinks * 140) {
      this.blinkAt = t;
      this.blinks += 1;
    }
    const blinking = this.blinkAt >= 0 && t - this.blinkAt < 0.12;
    if (this.blinkAt >= 0 && !blinking) this.blinkAt = -1;
    this.lid.set(lid, now, 280);
    this.px.set(look.x * 0.35, now, 280);
    this.py.set(look.y * 0.35, now, 280);
    this.pxR.set(look.x * 0.35, now + 60, 280);
    this.pyR.set(look.y * 0.35, now + 60, 280);
    this.scale.set(scale, now, 280);
    this.pupil.set(pupil, now, 280);
    this.brow.set(brow, now, 180);
    this.spark.set(spark, now, 120);
    this.wink.set(wink, now, 120);
    return {
      lid: Math.max(this.lid.at(now), blinking ? 1 : 0), wink: this.wink.at(now), px: this.px.at(now), py: this.py.at(now), pxR: this.pxR.at(now), pyR: this.pyR.at(now),
      scale: this.scale.at(now), pupil: this.pupil.at(now), brow: this.brow.at(now), spark: this.spark.at(now), blinking, look,
    };
  }
}

export interface EyeGeometry {
  /** eye centres, left then right */
  xs: [number, number];
  y: number;
  r: number;
  /** one CSS pixel in the caller's units */
  px: number;
  /** the body colour behind the lids (lids are the face, closed eyes are a line) */
  lidColor: string;
  /** mirrored drawing: pupil x offsets flip so a look stays in screen space */
  mirrored?: boolean;
  /** extra pupil y offset (thinking looks up) */
  lookUp?: number;
  closed?: boolean;
  dim?: boolean;
  /** pupil radius as a fraction of the iris radius (0.46 by default) */
  pupil?: number;
  /** how far the pupil may travel, as a fraction of the radius (0.35 by default; smaller eyes use less so the pupil never touches the outline) */
  travel?: number;
}

const INK = '#09090B';
const SNOW = '#FAFAFA';

/** Draw two eyes with the resolved frame. Call inside the character's transformed context. */
export function drawEyes(ctx: CanvasRenderingContext2D, g: EyeGeometry, f: EyeFrame) {
  const r = g.r * f.scale;
  let i = 0;
  for (const ex of g.xs) {
    const second = i === 1;
    i += 1;
    const lidHere = Math.max(f.lid, second && f.wink > 0.5 ? 1 : 0);
    const ey = g.y;
    if (g.closed || lidHere > 0.95) {
      // closed: a lid-coloured line (the body colour) with the ink outline, so it reads on the face
      ctx.lineCap = 'round';
      ctx.beginPath();
      ctx.moveTo(ex - r * 0.8, ey);
      ctx.lineTo(ex + r * 0.8, ey);
      ctx.lineWidth = Math.max(3.5 * g.px, r * 0.3);
      ctx.strokeStyle = g.dim ? 'rgba(9,9,11,0.55)' : INK;
      ctx.stroke();
      ctx.lineWidth = Math.max(1.5 * g.px, r * 0.3 - 2 * g.px);
      ctx.strokeStyle = g.lidColor;
      ctx.stroke();
      continue;
    }
    ctx.save();
    ctx.beginPath();
    ctx.arc(ex, ey, r, 0, Math.PI * 2);
    ctx.fillStyle = SNOW;
    ctx.fill();
    ctx.lineWidth = 1.5 * g.px; // a 1px outline lands on half pixels at 1×; 1.5px always leaves one solid ink pixel
    ctx.strokeStyle = INK;
    ctx.stroke();
    ctx.clip();
    const tv = (g.travel ?? 0.35) / 0.35;
    const ox = (second ? f.pxR : f.px) * r * tv * (g.mirrored ? -1 : 1);
    const oy = (second ? f.pyR : f.py) * r * tv + (g.lookUp ?? 0);
    const pr = r * (g.pupil ?? 0.46) * f.pupil;
    ctx.beginPath();
    ctx.arc(ex + 0.012 * r + ox, ey + oy, pr, 0, Math.PI * 2);
    ctx.fillStyle = INK;
    ctx.fill();
    if (f.spark > 0.02) {
      const sx = ex + 0.012 * r + ox;
      const sy = ey + oy;
      const a = pr * 0.9;
      ctx.fillStyle = `rgba(250,250,250,${f.spark})`;
      ctx.beginPath();
      ctx.moveTo(sx, sy - a);
      ctx.lineTo(sx + a * 0.25, sy - a * 0.25);
      ctx.lineTo(sx + a, sy);
      ctx.lineTo(sx + a * 0.25, sy + a * 0.25);
      ctx.lineTo(sx, sy + a);
      ctx.lineTo(sx - a * 0.25, sy + a * 0.25);
      ctx.lineTo(sx - a, sy);
      ctx.lineTo(sx - a * 0.25, sy - a * 0.25);
      ctx.closePath();
      ctx.fill();
    }
    if (lidHere > 0.02) {
      ctx.fillStyle = g.lidColor;
      ctx.fillRect(ex - r - g.px, ey - r - g.px, r * 2 + g.px * 2, r * 2 * lidHere);
      ctx.fillStyle = INK;
      ctx.fillRect(ex - r - g.px, ey - r + r * 2 * lidHere - g.px, r * 2 + g.px * 2, g.px);
    }
    ctx.restore();
    if (f.brow > 0.02) {
      ctx.save();
      ctx.lineWidth = Math.max(1.5 * g.px, r * 0.18);
      ctx.lineCap = 'round';
      ctx.strokeStyle = INK;
      ctx.globalAlpha = f.brow;
      const inward = ex < (g.xs[0] + g.xs[1]) / 2 ? 1 : -1;
      ctx.beginPath();
      ctx.moveTo(ex - r * 0.8, ey - r * 1.35 - r * 0.25 * f.brow);
      ctx.lineTo(ex + r * 0.8, ey - r * 1.35 + r * 0.2 * f.brow * (inward < 0 ? -1 : 1));
      ctx.stroke();
      ctx.restore();
    }
  }
}
